// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Universal methods to animate elements using animations defined in SCSS
 * BEM global modifiers which force an animation to trigger.
 * @author James Reid
 */

// @ts-check

// @@no-imports

// @@body
// Declare module-scoped global variables.
const NO_AUTO_FADE_ANIMATION = "--no-auto-animate"

/**
 * Fade given element in using a fade animation.
 * @param {HTMLElement} element Element to fade in.
 * @param {object} obj Optional destructured parameters object.
 * @param {string} [obj.animation] Name of fade in animation.
 * @returns {Promise.<HTMLElement>} Returned promise resolves with passed
 * element when fade in animation completes.
 */
const fadeElementIn = (element, { animation = "fade-in" } = {}) => {
    return new Promise(resolve => {
        if (!element.classList.contains("closed")) { return resolve(element) }

        const animationName = `--animate-${animation}`
        const animationListener = (/** @type {AnimationEvent} */ event) => {
            if (event.animationName !== animation) { return }

            element.removeEventListener("animationend", animationListener)
            element.classList.remove(animationName)

            resolve(element)
        }

        element.addEventListener("animationend", animationListener)
        element.classList.remove("closed")
        element.classList.add(NO_AUTO_FADE_ANIMATION, animationName)
    })
}

/**
 * Fade given element out using a fade animation.
 * @param {HTMLElement} element Element to fade out.
 * @param {object} obj Optional destructured parameters object.
 * @param {string} [obj.animation] Name of fade out animation.
 * @param {boolean} [obj.remove] Should element be removed from the DOM at the
 * end of the fade animation?
 * @returns {Promise.<HTMLElement>} Returned promise resolves with passed
 * element when fade out animation completes.
 */
const fadeElementOut = (element, {
    animation = "fade-out",
    remove = false
} = {}) => {
    return new Promise(resolve => {
        if (element.classList.contains("closed")) { return resolve(element) }

        const animationName = `--animate-${animation}`
        const animationListener = (/** @type {AnimationEvent} */ event) => {
            if (event.animationName !== animation) { return }

            element.removeEventListener("animationend", animationListener)
            element.classList.remove(NO_AUTO_FADE_ANIMATION, animationName)

            remove ? element.remove() : element.classList.add("closed")

            resolve(element)
        }

        element.addEventListener("animationend", animationListener)
        element.classList.add(animationName)
    })
}

/**
 * Toggle visibility of given element using a fade animation.
 * @param {HTMLElement} element Element to toggle visibility.
 * @param {object} obj Optional destructured parameters object.
 * @param {string} [obj.animationIn] Name of fade in animation.
 * @param {string} [obj.animationOut] Name of fade out animation.
 * @param {boolean} [obj.remove] Should element be removed from the DOM at the
 * end of the fade animation if the toggle action is closing the element?
 * @returns {Promise.<HTMLElement>} Returned promise resolves with passed
 * element when toggled animation completes.
 */
const fadeElementToggle = (element, {
    animationIn = "fade-in",
    animationOut = "fade-out",
    remove = false
} = {}) => {
    return element.classList.contains("closed")
        ? fadeElementIn(element, { animation: animationIn })
        : fadeElementOut(element, { animation: animationOut, remove })
}

/**
 * Cross fade between two elements, fading the first element out and then fading
 * the second element in.
 * @param {HTMLElement} fadeOutElement Element to fade out.
 * @param {HTMLElement} fadeInElement Element to fade in.
 * @param {object} obj Optional destructured parameters object.
 * @param {string} [obj.animationIn] Name of fade in animation.
 * @param {string} [obj.animationOut] Name of fade out animation.
 * @param {boolean} [obj.remove] Should element be removed from the DOM at the
 * end of the fade animation if the toggle action is closing the element?
 * @returns {Promise.<HTMLElement>} Returned promise resolves with element which
 * has been faded in when fade animations complete.
 */
const crossFadeElements = (fadeOutElement, fadeInElement, {
    animationIn = "fade-in",
    animationOut = "fade-out",
    remove = false
} = {}) => {
    return new Promise(resolve => {
        if (fadeOutElement.classList.contains("closed")) {
            if (remove) { fadeOutElement.remove() }
            return fadeElementIn(fadeInElement).then(resolve)
        }

        const fadeIn = `--animate-${animationIn}`
        const fadeInListener = (/** @type {AnimationEvent} */ event) => {
            if (event.animationName !== animationIn) { return }

            fadeOutElement.removeEventListener("animationend", fadeInListener)
            fadeOutElement.classList.remove(fadeIn)

            resolve(fadeInElement)
        }

        const fadeOut = `--animate-${animationOut}`
        const fadeOutListener = (/** @type {AnimationEvent} */ event) => {
            if (event.animationName !== animationOut) { return }

            fadeOutElement.removeEventListener("animationend", fadeOutListener)
            fadeOutElement.classList.remove(NO_AUTO_FADE_ANIMATION, fadeOut)

            remove ? fadeOutElement.remove()
                : fadeOutElement.classList.add("closed")

            fadeInElement.addEventListener("animationend", fadeInListener)
            fadeInElement.classList.remove("closed")
            fadeInElement.classList.add(NO_AUTO_FADE_ANIMATION, fadeIn)
        }

        fadeOutElement.addEventListener("animationend", fadeOutListener)
        fadeOutElement.classList.add(fadeOut)
    })
}

// @@exports
export { fadeElementIn, fadeElementOut, fadeElementToggle, crossFadeElements }
